import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT } from '../../util/types';

const PICKUP_FIELD_KEYS = ['pickup', 'pick_up', 'pickupoption'];
const DELIVERY_FIELD_KEYS = ['delivery', 'shipping', 'deliveryoption'];

const normalizeFieldKey = key => key?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';

const normalizeLabel = label => label?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';

const isPickupFieldKey = key => {
  const normalized = normalizeFieldKey(key);
  return PICKUP_FIELD_KEYS.includes(normalized) || normalized.includes('pickup');
};

const isDeliveryFieldKey = key => {
  const normalized = normalizeFieldKey(key);
  return DELIVERY_FIELD_KEYS.includes(normalized) || normalized.includes('delivery');
};

const isHowToGetItFieldKey = key => isPickupFieldKey(key) || isDeliveryFieldKey(key);

const isPickupDeliveryFieldLabel = label => {
  const normalized = normalizeLabel(label);
  return normalized.includes('pickup') || normalized.includes('delivery');
};

const isPickupDeliveryRelatedConfig = config => {
  const { key, showConfig = {} } = config;
  const { label } = showConfig;
  const normalizedKey = normalizeFieldKey(key);
  return (
    isHowToGetItFieldKey(key) ||
    normalizedKey.includes('pickup') ||
    normalizedKey.includes('delivery') ||
    isPickupDeliveryFieldLabel(label)
  );
};

const isPickupOption = (optionKey, optionLabel) => {
  const key = normalizeFieldKey(optionKey);
  const label = normalizeLabel(optionLabel);
  return key.includes('pickup') || label.includes('pickup');
};

const isDeliveryOption = (optionKey, optionLabel) => {
  const key = normalizeFieldKey(optionKey);
  const label = normalizeLabel(optionLabel);
  return key.includes('delivery') || label.includes('delivery');
};

const findFieldConfig = (listingFieldConfigs, key) =>
  listingFieldConfigs?.find(config => config.key === key);

/**
 * Collects custom listing field values to display as cards on the listing page.
 *
 * @param {Object} props
 * @param {Object} props.publicData
 * @param {Object} props.metadata
 * @param {Array} props.listingFieldConfigs
 * @param {Object} props.categoryConfiguration
 * @param {Object} props.intl
 * @returns {Array<{ key: string, label: string, value: string | number }>}
 */
export const getIncludedFieldItems = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);
  const listingType = publicData.listingType;

  const isFieldForSelectedCategories = fieldConfig =>
    isFieldForCategory(currentCategories, fieldConfig);

  const pickDetailFields = (filteredConfigs, config) => {
    if (isPickupDeliveryRelatedConfig(config)) {
      return filteredConfigs;
    }

    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);
    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && value != null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );
      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  const detailItems =
    listingFieldConfigs?.reduce(pickDetailFields, [])?.map(({ key, label, value }) => ({
      key,
      label,
      value,
    })) || [];

  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const otherItems = propsForCustomFields.reduce((items, fieldProps) => {
    const { schemaType, key, heading, options, selectedOptions, text } = fieldProps;
    const fieldConfig = findFieldConfig(listingFieldConfigs, key);

    if (fieldConfig && isPickupDeliveryRelatedConfig(fieldConfig)) {
      return items;
    }

    if (schemaType === SCHEMA_TYPE_MULTI_ENUM) {
      const selectedLabels = (selectedOptions || [])
        .map(optionKey => options?.find(o => o.key === optionKey)?.label)
        .filter(Boolean);
      return selectedLabels.length > 0
        ? [...items, { key, label: heading, value: selectedLabels.join(', ') }]
        : items;
    }

    if (schemaType === SCHEMA_TYPE_TEXT && text) {
      return [...items, { key, label: heading, value: text }];
    }

    return items;
  }, []);

  const seenKeys = new Set();
  return [...detailItems, ...otherItems].filter(item => {
    const fieldConfig = findFieldConfig(listingFieldConfigs, item.key);
    if (
      isHowToGetItFieldKey(item.key) ||
      (fieldConfig && isPickupDeliveryRelatedConfig(fieldConfig)) ||
      seenKeys.has(item.key)
    ) {
      return false;
    }
    seenKeys.add(item.key);
    return true;
  });
};

/**
 * Builds individual pickup/delivery option boxes for the How to get it section.
 *
 * @param {Object} props - Same shape as getIncludedFieldItems
 * @returns {Array<{ key: string, label: string, value: string }>}
 */
export const getHowToGetItOptionBoxes = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);
  const listingType = publicData.listingType;

  const availableLabel = intl.formatMessage({ id: 'ListingPage.pickupAvailable' });
  const boxes = [];
  const seenKeys = new Set();

  const addBox = (key, label, value) => {
    if (!label || seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);
    boxes.push({ key, label, value: value || availableLabel });
  };

  listingFieldConfigs?.forEach(config => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    if (!isTargetListingType || !isTargetCategory || !isPickupDeliveryRelatedConfig(config)) {
      return;
    }

    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (value == null) {
      return;
    }

    const { label: fieldLabel } = showConfig;

    if (schemaType === SCHEMA_TYPE_MULTI_ENUM) {
      const selectedValues = Array.isArray(value) ? value : [value];
      selectedValues.forEach(optionValue => {
        const optionConfig = enumOptions?.find(o => `${o.option}` === `${optionValue}`);
        const optionLabel = optionConfig?.label || String(optionValue);
        const boxKey = `${key}-${optionValue}`;
        addBox(boxKey, optionLabel, availableLabel);
      });
      return;
    }

    const detailValue = getDetailCustomFieldValue(
      enumOptions,
      value,
      schemaType,
      key,
      fieldLabel,
      intl,
      'ListingPage'
    );

    if (detailValue) {
      if (isPickupFieldKey(key) || isPickupOption(key, detailValue.label)) {
        addBox('pickup', intl.formatMessage({ id: 'ListingPage.pickupOption' }), String(detailValue.value));
      } else if (isDeliveryFieldKey(key) || isDeliveryOption(key, detailValue.label)) {
        addBox(
          'delivery',
          intl.formatMessage({ id: 'ListingPage.deliveryOption' }),
          String(detailValue.value)
        );
      } else {
        addBox(key, detailValue.label, String(detailValue.value));
      }
      return;
    }

    if (schemaType === SCHEMA_TYPE_TEXT) {
      addBox(key, fieldLabel, String(value));
    }
  });

  return boxes;
};

/**
 * @deprecated Use getHowToGetItOptionBoxes – kept for compatibility during refactor.
 */
export const getHowToGetItOptionItems = props => {
  const boxes = getHowToGetItOptionBoxes(props);
  const pickup = boxes.find(b => isPickupOption(b.key, b.label) || isPickupFieldKey(b.key)) || null;
  const delivery =
    boxes.find(b => isDeliveryOption(b.key, b.label) || isDeliveryFieldKey(b.key)) || null;
  return { pickup, delivery };
};
